// Shared meeting notes (Meeting Tools) — collaborative plain text over a CRDT.
// The document is a Yjs Y.Text; edits, cursors and presence sync as opaque
// binary frames over the engine's SEALED data channel (call-engine/notes.ts),
// so in an E2EE meeting nothing here is ever visible to our infrastructure.
// Ephemeral: a fresh doc per meeting, nothing persisted server-side.
//
// Like polls/timer this lives OUTSIDE the React tree — CodeMirror binds
// straight to `ytext` (see NotesTool), and the zustand view below carries only
// the chrome (mode / title / ready), so a keystroke never re-renders a tile.
//
// Sync model: peers exchange three frame kinds (see notes.ts) —
//   u  a Yjs document update (the edits)
//   a  an awareness update (cursors / selection)
//   q  a state vector: "I just joined, send me what I'm missing"
// A newcomer broadcasts `q` on init; every peer replies with the missing
// updates (+ its awareness) addressed only to the newcomer. Convergence is the
// CRDT's job, so order and dropped duplicates are harmless.
//
// Edit rights are the host's call: `everyone` (all edit) or `host-only` (host
// writes, others read). In host-only, a non-host is read-only AND does not
// broadcast its cursor — the gate lives here, at the transport, so a viewer's
// caret never leaks even though the editor still tracks it locally.

import type { NoteWire } from '@brizina/call-engine';
import {
  applyAwarenessUpdate,
  Awareness,
  encodeAwarenessUpdate,
  removeAwarenessStates,
} from 'y-protocols/awareness';
import * as Y from 'yjs';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import { engine } from '../engine';
import { callStore } from './callStore';

export type NotesMode = 'everyone' | 'host-only';

/** One person present in the notes (from Yjs awareness) — for the avatar row. */
export interface NotePeer {
  /** Yjs clientID: stable for the session, unique per editor. */
  key: number;
  name: string;
  color: string;
  isLocal: boolean;
}

const DEFAULT_TITLE = 'Meeting notes';

export interface NotesView {
  /** Everyone can edit, or only the host writes and others read (host's choice). */
  mode: NotesMode;
  /** Editable document name; drives the export filename. */
  title: string;
  /** True once a shared doc exists for the current meeting. */
  ready: boolean;
  /** Bumps each time the doc is (re)created, so the editor re-binds. */
  bindKey: number;
  /** Who's in the notes right now (drives the avatar row; colors match cursors). */
  peers: NotePeer[];
}

// Each editing SESSION gets its own color, derived from its Yjs clientID —
// unique even for the same account open in two tabs. The clientID is a random
// 32-bit int, so we run it through an avalanche hash (fmix32) before mapping to
// a hue: a linear multiplier aliases (distant ids can collide), a mixed hash
// scatters evenly across the wheel. y-codemirror reads user.color for the caret
// and user.colorLight for the selection; the presence avatars reuse the exact
// same value, so a person's dot always matches their cursor.
function colorForClient(clientID: number): { solid: string; light: string } {
  let h = clientID >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  const hue = h % 360;
  return { solid: `hsl(${hue} 72% 60%)`, light: `hsl(${hue} 72% 60% / 0.30)` };
}

/** Origin tag for updates we applied FROM the wire — never re-broadcast them. */
const REMOTE = Symbol('bz-notes-remote');

// The live CRDT handles. Recreated per meeting by initNotes(); handleNote and
// the editor always read the current ones. Seeded eagerly so they are never
// undefined (a stray frame before init lands on an idle doc — harmless).
let ydoc = new Y.Doc();
let ytext = ydoc.getText('body');
let ymeta = ydoc.getMap<string>('meta');
let awareness = new Awareness(ydoc);
let bound = false;
let heartbeat: ReturnType<typeof setInterval> | null = null;

const notesStore = createStore<NotesView>(() => ({
  mode: 'everyone',
  title: DEFAULT_TITLE,
  ready: false,
  bindKey: 0,
  peers: [],
}));

export function useNotesStore<T>(selector: (s: NotesView) => T): T {
  return useStore(notesStore, selector);
}

/** Current CRDT handles for the editor to bind (read fresh; they change on reset). */
export function notesHandles(): { ytext: Y.Text; awareness: Awareness } {
  return { ytext, awareness };
}

/** Rebuild the presence list from Yjs awareness (each state carries its user
 * name + color). Local editor first, then by clientID for a stable order. */
function refreshPeers(): void {
  const peers: NotePeer[] = [];
  awareness.getStates().forEach((state, clientID) => {
    const user = (state as { user?: { name?: string; color?: string } }).user;
    if (!user) return;
    peers.push({
      key: clientID,
      name: user.name || 'Someone',
      color: user.color || 'hsl(215 90% 62%)',
      isLocal: clientID === awareness.clientID,
    });
  });
  peers.sort((a, b) => (a.isLocal ? -1 : b.isLocal ? 1 : a.key - b.key));
  notesStore.setState({ peers });
}

/** Everyone-mode, or I'm the host: I may write and broadcast my cursor. */
export function canEditNotes(): boolean {
  return notesStore.getState().mode === 'everyone' || callStore.getState().isHost;
}

// ── Meta (mode + title) mirrored into the React view ─────────────────────────
function readMode(): NotesMode {
  return ymeta.get('mode') === 'host-only' ? 'host-only' : 'everyone';
}
function readTitle(): string {
  const t = ymeta.get('title');
  return typeof t === 'string' && t.trim() ? t : DEFAULT_TITLE;
}
function syncMetaToView(): void {
  notesStore.setState({ mode: readMode(), title: readTitle() });
}

// ── Local → wire ─────────────────────────────────────────────────────────────
function onLocalDocUpdate(update: Uint8Array, origin: unknown): void {
  if (origin === REMOTE) return; // applied from a peer — don't echo
  engine.sendNote({ kind: 'u', data: update });
}
function onLocalAwarenessUpdate(
  { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
  origin: unknown,
): void {
  if (origin === REMOTE) return;
  // A read-only viewer (host-only, not host) tracks its caret locally but never
  // broadcasts it — so viewer cursors never appear on anyone else's screen.
  if (!canEditNotes()) return;
  const changed = [...added, ...updated, ...removed];
  if (changed.length) engine.sendNote({ kind: 'a', data: encodeAwarenessUpdate(awareness, changed) });
}

// ── Wire → local (engine 'note' handler, wired once below) ───────────────────
function handleNote(_from: string, wire: NoteWire): void {
  if (wire.kind === 'u') {
    Y.applyUpdate(ydoc, wire.data, REMOTE);
  } else if (wire.kind === 'a') {
    applyAwarenessUpdate(awareness, wire.data, REMOTE);
  } else if (wire.kind === 'q') {
    // A peer's state vector: reply with exactly what it's missing, plus our
    // awareness, addressed only to it.
    const diff = Y.encodeStateAsUpdate(ydoc, wire.data);
    engine.sendNote({ kind: 'u', data: diff }, _from);
    const states = Array.from(awareness.getStates().keys());
    if (states.length) {
      engine.sendNote({ kind: 'a', data: encodeAwarenessUpdate(awareness, states) }, _from);
    }
  }
}

// ── Lifecycle ────────────────────────────────────────────────────────────────
function teardown(): void {
  if (heartbeat) {
    clearInterval(heartbeat);
    heartbeat = null;
  }
  if (bound) {
    ydoc.off('update', onLocalDocUpdate);
    awareness.off('update', onLocalAwarenessUpdate);
    awareness.off('change', refreshPeers);
    ymeta.unobserve(syncMetaToView);
    bound = false;
  }
  try {
    awareness.setLocalState(null);
  } catch {
    /* ignore */
  }
  awareness.destroy();
  ydoc.destroy();
}

/** Start a fresh, empty shared doc for a meeting. Called on entering the call. */
export function initNotes(): void {
  teardown();
  ydoc = new Y.Doc();
  ytext = ydoc.getText('body');
  ymeta = ydoc.getMap<string>('meta');
  awareness = new Awareness(ydoc);

  const cs = callStore.getState();
  const me = cs.localIdentity ? cs.attendees[cs.localIdentity] : undefined;
  const color = colorForClient(ydoc.clientID);
  awareness.setLocalStateField('user', {
    name: me?.name || 'You',
    color: color.solid,
    colorLight: color.light,
  });

  ydoc.on('update', onLocalDocUpdate);
  awareness.on('update', onLocalAwarenessUpdate);
  awareness.on('change', refreshPeers);
  ymeta.observe(syncMetaToView);
  bound = true;
  refreshPeers();

  notesStore.setState((s) => ({
    ready: true,
    bindKey: s.bindKey + 1,
    mode: readMode(),
    title: readTitle(),
  }));

  // Pull state from anyone already in the meeting (we may be the late joiner).
  engine.sendNote({ kind: 'q', data: Y.encodeStateVector(ydoc) });

  // Keep our presence fresh and prune peers that vanished without a clean exit.
  heartbeat = setInterval(() => {
    const st = awareness.getLocalState();
    if (st && canEditNotes()) awareness.setLocalState(st);
    const now = Date.now();
    const stale: number[] = [];
    awareness.getStates().forEach((_s, id) => {
      const m = awareness.meta.get(id);
      if (id !== awareness.clientID && m && now - m.lastUpdated > 30_000) stale.push(id);
    });
    if (stale.length) removeAwarenessStates(awareness, stale, 'timeout');
  }, 15_000);
}

/** Tear the doc down on leaving the call — notes are ephemeral. */
export function resetNotes(): void {
  teardown();
  ydoc = new Y.Doc();
  ytext = ydoc.getText('body');
  ymeta = ydoc.getMap<string>('meta');
  awareness = new Awareness(ydoc);
  notesStore.setState((s) => ({ mode: 'everyone', title: DEFAULT_TITLE, ready: false, bindKey: s.bindKey + 1, peers: [] }));
}

// ── Host / editor actions ────────────────────────────────────────────────────
/** Host only: choose whether everyone can edit or only the host writes. */
export function setNotesMode(mode: NotesMode): void {
  if (!callStore.getState().isHost) return;
  ymeta.set('mode', mode);
  // If we just locked to host-only, drop any viewer cursors we were showing.
  syncMetaToView();
}

/** Rename the notes doc (drives the export filename). Editors only. */
export function setNotesTitle(title: string): void {
  if (!canEditNotes()) return;
  ymeta.set('title', title.slice(0, 120));
}

/** Download the current notes as Markdown or plain text (ephemeral export). */
export function exportNotes(format: 'md' | 'txt'): void {
  const text = ytext.toString();
  const title = readTitle();
  const body = format === 'md' ? `# ${title}\n\n${text}\n` : `${title}\n\n${text}\n`;
  const mime = format === 'md' ? 'text/markdown' : 'text/plain';
  const url = URL.createObjectURL(new Blob([body], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-z0-9]+/gi, '-').slice(0, 48) || 'meeting-notes'}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

// Wire the engine handler once on import (this module loads for every call via
// CallView, which also drives init/reset).
engine.events.on('note', handleNote);
